import { useState, useEffect, useRef } from "react";
import { Switch, TextField, ListItemIcon, ListItemText, Autocomplete } from "@mui/material";
// cmps
import Svg from "./svg";
// hooks
import useSend from "../hooks/useSend";
import useStorage, { StorageKeys } from "../hooks/useStorage";
// services
import User from "../services/user";
import Constants from "../services/constants";
// types
import type { ChangeEvent, SyntheticEvent } from "react";
import type { ReadonlyDeep, ValueOf } from "type-fest";
import type { CmdSearch, CmdSearchB2CUser } from "../types/gupport";
import type { SearchDataGateway, SearchDataUser } from "../types/gupport";
import type { SearchOption } from "../types/misc";

type SearchData = {
	gateway: SearchDataGateway;
	user: SearchDataUser;
};

type Props = Readonly<{
	placeholder: string;
	value: string;
	kind: ValueOf<typeof Constants.Kind>;
	onItemSelected: (option: SearchOption) => void;
	onClear: () => void;
}>;

const DEFAULT_OPTION = {
	id: "",
	label: "",
	iconPath: "",
	primary: "",
	secondary: "",
} as const satisfies SearchOption;

const SearchAutocomplete = ({ placeholder, value, kind, onItemSelected, onClear }: Props) => {
	const send = useSend();
	const timeoutIdRef = useRef<number | undefined>(undefined);

	const [advancedSearchEnabled, setAdvancedSearchEnabled] = useStorage(StorageKeys.isAdvancedSearchEnabled);

	const [searchText, setSearchText] = useState(value);
	const [optionsLoading, setOptionsLoading] = useState(false);
	const [options, setOptions] = useState<ReadonlyArray<SearchOption>>([]);
	const [selectedItem, setSelectedItem] = useState<SearchOption>({ ...DEFAULT_OPTION, label: value });

	useEffect(() => (
		() => {
			window.clearTimeout(timeoutIdRef.current);
		}
	), []);

	useEffect(() => {
		if (value) {
			setSearchText(value);
			setSelectedItem({ ...DEFAULT_OPTION, label: value });
		}
	}, [value]);

	const handleToggleChange = (event: ChangeEvent<HTMLInputElement>) => {
		setAdvancedSearchEnabled(event.target.checked);
	};

	const createOptions = (item: ReadonlyDeep<SearchData[typeof kind]>): SearchOption => {
		if (item.kind === Constants.Kind.User) {
			const primary = (item.name !== null && item.name.length > 0) ? item.name : item.username;
			return {
				id: item.id,
				label: `${primary} (${item.username})`,
				iconPath: "add/add-user.svg",
				primary: primary,
				secondary: item.username,
			} as const satisfies SearchOption;
		} else if (item.kind === Constants.Kind.Gateway) {
			const primary = (item.label.length > 0) ? item.label : item.id;
			return {
				id: item.id,
				label: `${primary} (${item.id}) [${item.code}]`,
				iconPath: "add/add-hub.svg",
				primary: primary,
				secondary: `${item.id} [${item.code}]`,
			} as const satisfies SearchOption;
		} else {
			return DEFAULT_OPTION;
		}
	};

	const handleInputChange = (_event: SyntheticEvent, value: string/*, reason: AutocompleteInputChangeReason*/) => {
		setSearchText(value);
		window.clearTimeout(timeoutIdRef.current);

		if (value.length >= 3) {
			timeoutIdRef.current = window.setTimeout(() => {
				setOptionsLoading(true);

				if (advancedSearchEnabled) {
					const cmd = {
						action: "searchB2CUser",
						query: value,
					} as const satisfies CmdSearchB2CUser;
					send(cmd, (error, msg) => {
						if (!error && msg?.payload.status === "ok") {
							const newOptions = msg.payload.data
								.filter((item, index, array) => (array.findIndex((el) => (el.id === item.id)) === index)) // TODO: remove deduplication in future
								.map((item) => createOptions(item));

							setOptions(newOptions);
						} else {
							setOptions([]);
						}
						setOptionsLoading(false);
					});
				} else {
					const cmd = {
						action: "search",
						kind: kind,
						query: value,
					} as const satisfies CmdSearch;
					send(cmd, (error, msg) => {
						if (!error && msg?.payload.status === "ok") {
							const newOptions = msg.payload.data
								.filter((item, index, array) => (array.findIndex((el) => (el.id === item.id)) === index)) // TODO: remove deduplication in future
								.map((item) => createOptions(item));

							setOptions(newOptions);
						} else {
							setOptions([]);
						}
						setOptionsLoading(false);
					});
				}
			}, 500);
		} else {
			setOptions([]);
		}
	};

	const handleChange = (_event: SyntheticEvent, value: SearchOption | null/*, reason: AutocompleteChangeReason, details: AutocompleteChangeDetails*/) => {
		if (value === null) {
			setSearchText("");
			setOptions([]);
			setSelectedItem(DEFAULT_OPTION);
			onClear();
		} else {
			setSearchText(value.label);
			setSelectedItem(value);
			onItemSelected(value);
		}
	};

	return (
		<div style={{ display: "flex", position: "relative" }}>
			<Autocomplete
				id="autocomplete_search"
				color="primary"
				fullWidth={true}
				loading={optionsLoading}
				options={options}
				filterOptions={(options/*, state*/) => (options)}
				isOptionEqualToValue={(option, value) => (option.id === value.id)}
				inputValue={searchText}
				onInputChange={handleInputChange}
				value={selectedItem}
				onChange={handleChange}
				renderInput={(params) => (
					<TextField autoFocus={true} {...params} placeholder={placeholder} />
				)}
				renderOption={({ key, ...optionProps }, option/*, state, ownerState*/) => (
					<li key={key} {...optionProps}>
						<ListItemIcon><Svg src={option.iconPath} /></ListItemIcon>
						<ListItemText primary={option.primary} secondary={option.secondary} />
					</li>
				)}
				style={{ marginRight: "24px" }}
			/>
			{User.hasLevel("user_b2c_read") && <Switch checked={advancedSearchEnabled} onChange={handleToggleChange} />}
		</div>
	);
};

export default SearchAutocomplete;
